import { Controller } from "@hotwired/stimulus"
import { POLL_PALIERS, POLL_ABANDON_MS, MESSAGE_ABANDON, messageEncodage } from "../lib/video_status_message"

export default class extends Controller {
  static targets = [ "fileInput", "progress", "status", "videoIdInput" ]
  static values = { lessonId: Number }

  async upload(event) {
    event.preventDefault()
    const file = this.fileInputTarget.files[0]
    if (!file) return

    this.setStatus("Préparation…")

    try {
      const created = await this.fetchJSON("/admin/videos", {
        method: "POST",
        body: JSON.stringify({ filename: file.name }),
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrf() }
      })

      this.setStatus("Envoi en cours…")
      await this.uploadToR2(created.upload_url, file)

      this.setStatus("Mise en file d'attente…")
      await this.fetchJSON(`/admin/videos/${created.video_id}/encode`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrf() }
      })

      if (this.hasVideoIdInputTarget) {
        this.videoIdInputTarget.value = created.video_id
      }

      await this.pollStatus(created.video_id)
    } catch (e) {
      this.setStatus(`Erreur: ${e.message}`)
    }
  }

  async uploadToR2(url, file) {
    const xhr = new XMLHttpRequest()
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100)
          if (this.hasProgressTarget) this.progressTarget.value = pct
          this.setStatus(`Envoi ${pct} %`)
        }
      })
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve()
        else reject(new Error(`R2 upload failed: ${xhr.status}`))
      })
      xhr.addEventListener("error", () => reject(new Error("R2 upload network error")))
      xhr.open("PUT", url)
      xhr.setRequestHeader("Content-Type", file.type)
      xhr.send(file)
    })
  }

  // ⛔ Ce suivi tournait à `setInterval(…, 5000)` SANS aucun abandon : un
  // onglet laissé ouvert interrogeait le serveur toutes les 5 s jusqu'au
  // matin, et une vidéo bloquée sur `processing` (le job n'a aucun
  // `retry_on`) ne l'arrêtait jamais. Le défaut avait été corrigé dans
  // course_editor/lib/api.js le 26/08/2026 — mais pas ici, parce que rien ne
  // reliait les deux uploaders. Ils partagent maintenant lib/video_status_message.
  //
  // ⛔ Et `setInterval` était doublement faux : il ne compte pas le temps de
  // la requête. Sur un serveur lent, les appels se recouvrent. Le `setTimeout`
  // replanifié ci-dessous n'attend qu'APRÈS avoir eu sa réponse.
  async pollStatus(videoId) {
    return new Promise((resolve, reject) => {
      const debut = Date.now()
      let stopped = false
      let handle = null

      const stop = () => { stopped = true; if (handle) clearTimeout(handle) }

      const replanifier = () => {
        if (stopped) return
        const ecoule = Date.now() - debut
        if (ecoule >= POLL_ABANDON_MS) {
          stop()
          this.setStatus(MESSAGE_ABANDON)
          reject(new Error(MESSAGE_ABANDON))
          return
        }
        handle = setTimeout(tick, POLL_PALIERS.find((p) => ecoule < p.jusqua).intervalle)
      }

      const tick = async () => {
        if (stopped) return
        try {
          const data = await this.fetchJSON(`/admin/videos/${videoId}/status`)
          if (data.status === "ready") {
            stop()
            this.setStatus("Vidéo prête ✓")
            this.dispatch("ready", { detail: data })
            resolve(data)
            return
          } else if (data.status === "failed") {
            stop()
            this.setStatus(`Échec encodage: ${data.error_message || ""}`)
            reject(new Error(data.error_message || "encoding failed"))
            return
          }
          this.setStatus(messageEncodage(data))
        } catch (e) {
          stop()
          reject(e)
          return
        }
        replanifier()
      }

      tick() // premier appel tout de suite
    })
  }

  async fetchJSON(url, options = {}) {
    const res = await fetch(url, options)
    if (!res.ok) throw new Error(await this.errorMessage(res))
    return res.json()
  }

  // Never paste a raw response body into the UI. A 500 returns a full HTML
  // error page, and dumping it into the status label is what made an upload
  // failure look like garbled text instead of a problem worth reporting.
  async errorMessage(res) {
    try {
      const data = await res.json()
      if (data && data.error) return data.error
    } catch (_) {
      // Not JSON — fall through to the generic message.
    }
    return `Échec de l'envoi (erreur ${res.status}). Réessayez dans quelques minutes.`
  }

  csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  setStatus(msg) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg
  }
}
